import { Box, Flex, Text } from '@chakra-ui/react';
import { useRouter } from 'next/navigation';
import React, { memo, useCallback, useEffect, useRef } from 'react';

import { useColorModeValue } from '@components/ui/color-mode';
import NavigationEntityIcon from '@features/navigation/NavigationEntityIcon';
import { useEntityToggleState } from '@features/navigation/ToggleStateProvider';
import {
  NavigationEntity,
  useIsSelected,
  useSidebarNavigationEntities,
} from 'hooks/useNavigationEntities';
import RawArrowIcon from 'vectors/Arrowhead';

interface Props {
  entity: NavigationEntity;
  depth?: number;
  onSelect?: (entity: NavigationEntity) => void;
}

export const NavigationItem: React.FC<Props> = React.memo(({ entity, depth = 0, onSelect }) => {
  const isSelected = useIsSelected(entity);
  const [isToggled, toggle] = useEntityToggleState(entity);
  const isDark = useColorModeValue(false, true);
  const router = useRouter();

  const onClick = useCallback(() => {
    if (entity.absolutePath != null && !isSelected) {
      router.push(entity.absolutePath);
      if (!isToggled) {
        toggle();
      }
      onSelect?.(entity);
    }
  }, [entity, toggle, router, isSelected, isToggled, onSelect]);

  useEffect(() => {
    const path = entity.absolutePath;
    if (path != null) {
      router.prefetch(path);
    }
  }, [entity, router]);

  const { byId: entitiesById } = useSidebarNavigationEntities();
  const itemRef = useRef<HTMLDivElement>(null);

  let background: string | undefined;
  if (isSelected) {
    background = 'sidebar.hover';
  }

  const indentationPaddingLeft = `${depth * 8}px`;
  return (
    <>
      <Box data-testid="nav-item" position="relative">
        <Flex
          ref={itemRef}
          data-testid={`nav-${entity.name}`}
          tabIndex={-1}
          role="navigation"
          alignItems="center"
          marginX={1}
          borderRadius="md"
          cursor="pointer"
          height={7}
          transition="background .2s"
          bgColor={background}
          color={isDark ? (isSelected ? 'surface.background' : 'text.primary') : undefined}
          _hover={
            isDark ? { bgColor: 'sidebar.hover', color: 'white' } : { bgColor: 'sidebar.hover' }
          }
          onClick={onClick}
        >
          {depth > 0 && <div style={{ paddingLeft: indentationPaddingLeft }} />}
          <Flex flex="1" gap="2px" overflow="hidden">
            <EmptySpace depth={depth} />
            <Flex alignItems="center" justifyContent="center" minHeight={7} minWidth={7}>
              <NavigationEntityIcon entity={entity} />
            </Flex>
            <Flex
              flex="1"
              fontSize="sm"
              fontWeight={depth === 0 ? 'medium' : undefined}
              alignItems="center"
              color="text.primary"
            >
              <Text wordBreak="break-all">{entity.name}</Text>
              {entity.children.length > 0 && <ArrowIcon open={isToggled} onClick={toggle} />}
            </Flex>
          </Flex>
        </Flex>
      </Box>
      {isToggled && (
        <>
          {entity.children.map((child) => {
            const childEntity = entitiesById[child.id];
            if (childEntity == null) {
              return null;
            }
            return (
              <NavigationItem
                key={`${childEntity.id}`}
                entity={childEntity}
                depth={depth + 1}
                onSelect={onSelect}
              />
            );
          })}
        </>
      )}
    </>
  );
});

NavigationItem.displayName = 'NavigationItem';

const EmptySpace = React.memo(({ depth }: { depth: number }) => {
  return <div style={{ paddingLeft: depth === 0 ? 0 : '4px' }} />;
});

export const ArrowIcon = memo(({ open, onClick }: { open: boolean; onClick: () => void }) => {
  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      width="16px"
      mr="-4px"
      color="text.primary"
      borderRadius="md"
      cursor="pointer"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      <RawArrowIcon boxSize="6px" direction={open ? 'down' : 'right'} />
    </Flex>
  );
});

EmptySpace.displayName = 'EmptySpace';
